/**
 * @module Fuel HOC
 * @description Central source providers, avoids having multiple nested providers.
 */
import { createContext, useEffect, useState } from "react";
import { ErrorBoundary } from "@red-digital/bricks";
import ErrorBoundaryComponent from "@/blocks/ErrorBoundary";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3"; // ReCaptcha Provider used to generate the token we pass in the 'frontierSiteDetailPredictive' cookie
import requestMonarch from "@/js/helpers/monarch";
import { useRouter } from "next/router";
import {
  isCartHeavy,
  isPhoneHeavy,
  isResumeQuote,
  updateOLVersion,
} from "./utils/feature-flagging";
import useCartState from "@/hooks/useCartState";
import { getCookie } from "@/js/utils/cookie";
import { DEFAULT_OPEN_HOURS } from "@/js/constants/landingPage";
import { MONARCH_RULESETS } from "@/js/constants/monarch";
import {
  DEFAULT_FUSE_NUMBER,
  MONARCH_SOURCE,
  MONARCH_TOKEN,
  SOURCE_KEY,
  WRITE_KEY,
} from "@/js/constants/cohesion";

/**
 * @function FuelContext
 * @description
 */
export const FuelContext = createContext();

/**
 * @function withFuel
 * @description
 */
export const withFuel = ({ App }) => {
  const FuelProviderHOC = (props) => {
    const router = useRouter();
    const { clickThrough } = useCartState();

    const [monarchProduct, setMonarchProduct] = useState("fiber-500m");
    const [monarchCartHeavy, setMonarchCartHeavy] = useState(false);
    const [monarchPhoneHeavy, setMonarchPhoneHeavy] = useState(false);
    const [monarchResumeQuote, setMonarchResumeQuote] = useState(false);
    const [monarchOLVersion, setMonarchOLVersion] = useState("v2");
    const [monarchOpenHours, setMonarchOpenHours] = useState([]);
    const [fuseNumber, setFuseNumber] = useState(null);
    const [isVerizon, setIsVerizon] = useState(false);

    const [formInputted, setFormInputted] = useState(false);
    const [suggestionsVisible, setSuggestionsVisible] = useState(false);

    useEffect(() => {
      cohesion("ready", () => {
        const apiVersion = getCookie("apiVersion");
        requestMonarch(
          "ruleset",
          MONARCH_RULESETS.LANDING_PAGE,
          { olVersion: apiVersion },
          {
            writeKey: WRITE_KEY,
            makeSourceUid: SOURCE_KEY,
            sourceId: MONARCH_SOURCE,
            token: MONARCH_TOKEN,
          },
          (response) => {
            if (!response) return;

            const {
              cartHeavy,
              phoneHeavy,
              determineOLVersion,
              attendHours,
              determineProduct,
              determineResumeQuote,
              verizonView,
            } = response;

            if (typeof determineProduct === "string")
              setMonarchProduct(determineProduct);

            if (isCartHeavy(cartHeavy, router.query)) setMonarchCartHeavy(true);

            if (isPhoneHeavy(phoneHeavy, router.query))
              setMonarchPhoneHeavy(true);

            if (verizonView) setIsVerizon(true);

            if (isResumeQuote(determineResumeQuote))
              setMonarchResumeQuote(true);

            if (updateOLVersion(determineOLVersion))
              setMonarchOLVersion(determineOLVersion);

            if (attendHours?.data && Array.isArray(attendHours?.data)) {
              setMonarchOpenHours(attendHours.data);
            } else {
              setMonarchOpenHours(DEFAULT_OPEN_HOURS);
            }
          },
        );
      });
    }, [router.query]);

    useEffect(() => {
      window?.cohesion("fuse:leasesReceived", (leases) => {
        setFuseNumber(leases?.[0]?.dnis);
      });

      window?.cohesion("fuse:error", () => setFuseNumber(DEFAULT_FUSE_NUMBER));
    }, []);

    return (
      <FuelContext.Provider
        value={{
          monarchProduct,
          cartHeavy: monarchCartHeavy,

          phoneHeavy: monarchPhoneHeavy,

          clickThrough,
          resumeQuote: monarchResumeQuote,

          olVersion: monarchOLVersion,
          formInputted,
          setFormInputted,
          suggestionsVisible,
          setSuggestionsVisible,
          openHours: monarchOpenHours,
          fuseNumber,
          verizonView: isVerizon,
        }}
      >
        <ErrorBoundary
          production={process.env.environment === "production"}
          component={ErrorBoundaryComponent}
        >
          <GoogleReCaptchaProvider
            useEnterprise
            reCaptchaKey={`${process.env.reCaptchaSiteKey}`}
            scriptProps={{ defer: true }}
          >
            <App {...props} />
          </GoogleReCaptchaProvider>
        </ErrorBoundary>
      </FuelContext.Provider>
    );
  };

  return FuelProviderHOC;
};

export default withFuel;
